import { ContentSwitcher } from '@app/components/various/ContentSwitcher';
import { getFirstUnit, getProductUnits, getUnitSwitcherName } from '@app/logic/unitsSwitch';
import { useUnitSwitcher } from '@app/utils/hooks/unitSwitcher/useUnitSwitcher';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Product } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

interface Props {
  defaultUnit?: string;
  tableMappings: Product.UnitMappings;
  switcherName?: string;
}

export const UnitSwitcher = ({ defaultUnit, tableMappings, switcherName }: Props) => {
  const { t } = useTranslation(['common', 'products']);
  const dispatch = useDispatch();
  const switchName = getUnitSwitcherName(tableMappings);
  const { selectedUnits, setSelectedUnits } = useUnitSwitcher();
  const selectedUnit = selectedUnits[switchName];

  React.useEffect(() => {
    if (isDefined(selectedUnit)) {
      return;
    }

    const hasDefaultUnit = isDefined(defaultUnit) && !isEmpty(defaultUnit);

    setSelectedUnits({
      ...selectedUnits,
      [getUnitSwitcherName(tableMappings)]: hasDefaultUnit ? defaultUnit : getFirstUnit(tableMappings),
    });
  }, [defaultUnit, selectedUnit, selectedUnits, setSelectedUnits, tableMappings]);

  const onChange = React.useCallback(
    (value: string) => {
      setSelectedUnits({
        ...selectedUnits,
        [getUnitSwitcherName(tableMappings)]: value,
      });
    },
    [dispatch, setSelectedUnits, selectedUnits, tableMappings],
  );

  const groupName = isDefined(switcherName) ? `${t('common:unit_one')} - ${switcherName}` : t('common:unit_one');

  return (
    <div className="flex items-center gap-4">
      <span className="text-nowrap text-neutral-60 body-5">{t('products:switch_size_localization')}</span>
      <ContentSwitcher
        tabs={getProductUnits(tableMappings).map(unit => ({
          title: unit,
          value: unit,
        }))}
        groupName={groupName}
        selectedValue={selectedUnit}
        onChange={onChange}
        variant="gray"
        keepEvenWidth
      />
    </div>
  );
};
